import {
  ChevronRight,
  Download,
  Search,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { formatDate, MappingStatus, MappingStyles } from "../../utils/utils"; // Import MappingStatus and MappingStyles
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../datepicker.css"
const ITEMS_PER_PAGE = 5; // Define items per page for pagination

const ApplicationTable = ({ activeApplications }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [pickerMode, setPickerMode] = useState("date");
  const [startDate, endDate] = dateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const statusFilterOptions = [
    { value: "All", label: "All Statuses" },
    { value: "processing", label: "Processing" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  useEffect(() => {
    setSelectedApplicants([]);
    setSelectedAll(false);
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchTerm, dateRange, selectedStatus, activeApplications]);

  const filteredApplicants = activeApplications.filter((app) => {
    const applicantName = app.loan?.user?.name || "";
    const appStatus = app.status || "";
    const appCreationDate = app.sentAt || app.createdAt || "";

    const matchesSearch = applicantName
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) || app?.loanType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      selectedStatus === "All" ||
      appStatus.toLowerCase() === selectedStatus.toLowerCase();

    let matchesDate = true;
    if (startDate && endDate && appCreationDate) {
      try {
        const dateOfApp = new Date(appCreationDate);
        if (!isNaN(dateOfApp.getTime())) {
          const adjustedEndDate = new Date(endDate);
          adjustedEndDate.setHours(23, 59, 59, 999);
          matchesDate = dateOfApp >= startDate && dateOfApp <= adjustedEndDate;
        }
      } catch (error) {
        console.warn("Invalid date format for filtering:", appCreationDate);
        matchesDate = false;
      }
    }
    return matchesSearch && matchesStatus && matchesDate;
  });

  useEffect(() => {
    setTotalPages(Math.ceil(filteredApplicants.length / ITEMS_PER_PAGE));
  }, [filteredApplicants]);

  const handleCheckboxChange = (appId) => {
    if (selectedApplicants.includes(appId)) {
      setSelectedApplicants(selectedApplicants.filter((id) => id !== appId));
    } else {
      setSelectedApplicants([...selectedApplicants, appId]);
    }
  };

  const handleSelectAll = () => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    setSelectedApplicants(
      newSelectedAll ? filteredApplicants.map((app) => app._id) : []
    );
  };
  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };
  const exportToExcelAndZip = async () => {
    const applicationsToExport =
      selectedApplicants.length > 0
        ? filteredApplicants.filter((app) =>
            selectedApplicants.includes(app._id)
          )
        : filteredApplicants;

    const wb = XLSX.utils.book_new();
    const dataForExcel = applicationsToExport.map((app) => ({
      "Application ID": app._id,
      "Applicant Name": app.loan?.user?.name || "",
      "Loan Amount": app.loan?.principle,
      "Loan Type": app.loan?.loanType,
      Status: app.status,
      "Sent/Created At": formatDate(app.sentAt || app.createdAt),
      "Assigned To (CM)": app.loan?.assignedCreditManager
        ? `CM ID: ${app.loan.assignedCreditManager}`
        : "N/A",
    }));
    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "Applications");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("applications.xlsx", excelBuffer);
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "applications.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleStatusFilterChange = (statusValue) => {
    setSelectedStatus(statusValue);
    setIsStatusDropdownOpen(false);
  };

  const paginatedDisplayApplicants = filteredApplicants.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };
  return (
    <div className="text-white mt-3">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Active Applications</h3>
        <div
          onClick={() => navigate("/banking-partner/customers")}
          className="flex items-center text-[#FFCA20] cursor-pointer hover:underline"
        >
          Go to All Loan Applications
          <ChevronRight size={18} className="ml-1" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative w-full sm:flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Applicant Name"
            className="bg-transparent border border-[#333333] rounded-lg py-2 pl-10 pr-4 w-full text-sm text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative bg-transparent rounded-lg border-2 border-[#333333]">
          <button
            className="flex items-center px-3 py-2 focus:outline-none gap-2"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          >
            <svg
              width="14"
              height="16"
              viewBox="0 0 14 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C8 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                fill="#FFCA20"
              />
            </svg>
            {startDate && endDate
              ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
              : getPickerLabel()}
          </button>

          {isDatePickerOpen && (
            <div className="absolute z-10 mt-2 bg-[#1e1e1e] rounded-md p-3">
              {/* Picker Mode Switch */}
              <div className="flex gap-2 mb-2">
                <button
                  className={`px-3 py-1 rounded ${
                    pickerMode === "date"
                      ? "bg-yellow-500 text-black"
                      : "bg-[#333] text-white"
                  }`}
                  onClick={() => setPickerMode("date")}
                >
                  Date
                </button>
                <button
                  className={`px-3 py-1 rounded ${
                    pickerMode === "month"
                      ? "bg-yellow-500 text-black"
                      : "bg-[#333] text-white"
                  }`}
                  onClick={() => setPickerMode("month")}
                >
                  Month
                </button>
                <button
                  className={`px-3 py-1 rounded ${
                    pickerMode === "year"
                      ? "bg-yellow-500 text-black"
                      : "bg-[#333] text-white"
                  }`}
                  onClick={() => setPickerMode("year")}
                >
                  Year
                </button>
              </div>

              {/* Calendar */}
              <DatePicker
                selected={startDate}
                onChange={(update) => {
                  if (pickerMode === "date") {
                    setDateRange(update);
                    if (update[0] && update[1]) setIsDatePickerOpen(false);
                  } else {
                    setDateRange([update, update]);
                    setIsDatePickerOpen(false);
                  }
                }}
                startDate={startDate}
                endDate={endDate}
                selectsRange={pickerMode === "date"}
                showMonthYearPicker={pickerMode === "month"}
                showYearPicker={pickerMode === "year"}
                inline
              />
            </div>
          )}
        </div>

        <div className="relative border border-[#333333] rounded-md">
          <button
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            className="bg-transparent border border-[#333333] rounded-lg py-2 px-3 flex items-center gap-2 text-sm text-white hover:border-gray-500"
          >
            {statusFilterOptions.find((opt) => opt.value === selectedStatus)
              ?.label || "Status"}
            <ChevronDown size={16} className="text-gray-400" />
          </button>
          {isStatusDropdownOpen && (
            <div className="absolute z-20 mt-1 w-32 bg-[#1e1e1e] rounded-md shadow-lg border border-[#333333]">
              {statusFilterOptions.map((option) => (
                <button
                  key={option.value}
                  className={`block w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2c2c2c] ${
                    selectedStatus === option.value ? "bg-[#2c2c2c]" : ""
                  }`}
                  onClick={() => handleStatusFilterChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={exportToExcelAndZip}
          className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold flex items-center gap-2"
        >
          Download as ZIP <Download size={16} />
        </button>
      </div>

      <div className="bg-[#131313] rounded-lg overflow-hidden border border-[#333333]">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="min-w-full divide-y divide-gray-800 rounded-lg">
            <thead className=" text-white rounded-lg">
              <tr>
                <th className="p-4 text-center border-r border-[#333333] rounded-lg">
                  <input
                    type="checkbox"
                    className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer before:content-['✔'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                    checked={selectedAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="p-4 text-center border-r border-[#333333]">
                  APPLICATION ID
                </th>
                <th className="p-4 text-center border-r border-[#333333]">
                  APPLICANT NAME
                </th>
                <th className="p-4 text-center border-r border-[#333333]">
                  LOAN AMOUNT
                </th>
                <th className="p-4 text-center border-r border-[#333333]">
                  ASSIGNED DATE
                </th>
                <th className="p-4 text-center border-r border-[#333333]">
                  STATUS
                </th>
                <th className="p-4 text-center border-r border-[#333333]">
                  APPLICATION
                </th>
                <th className="p-4 text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {paginatedDisplayApplicants.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="p-6 text-center text-gray-400 border-none"
                  >
                    No rows found
                  </td>
                </tr>
              ) : (
                paginatedDisplayApplicants.map((application) => (
                  <tr key={application._id} className=" text-white ">
                    <td className="p-4 text-center border-r border-[#333333]">
                      <input
                        type="checkbox"
                        className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer before:content-['✔'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                        checked={selectedApplicants.includes(application._id)}
                        onChange={() => handleCheckboxChange(application._id)}
                      />
                    </td>
                    <td className="p-4 text-center border-r border-[#333333]">
                      <div>{application._id}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(
                          application.sentAt || application.createdAt
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-center border-r border-[#333333]">
                      {application.loan?.user?.name || "N/A"}
                    </td>
                    <td className="p-4 text-center border-r border-[#333333]">
                      <div>
                        ₹
                        {application.loan?.principle?.toLocaleString("en-IN") ||
                          "N/A"}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {application.loan?.loanType || "N/A"}
                      </div>
                    </td>
                    <td className="p-4 text-center border-r border-[#333333]">
                      <div className="flex items-center justify-center gap-1">
                        {formatDate(application.updatedAt)}
                      </div>
                    </td>
                    <td className="p-4 text-center border-r border-[#333333]">
                      <span
                        className={`px-3 py-1 rounded-full text-sm ${MappingStyles(
                          // Use MappingStyles
                          application.status
                        )}`}
                      >
                        {MappingStatus(application.status)}{" "}
                        {/* Use MappingStatus */}
                      </span>
                    </td>
                    <td className="p-4 text-center border-r border-[#333333]">
                      <button
                        onClick={() =>
                          navigate(
                            `/banking-partner/loan-applications/${application._id}`
                          )
                        }
                        className="text-yellow-400 hover:underline"
                      >
                        View
                      </button>
                    </td>

                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-2">
                        <Download size={16} className="text-yellow-500 mt-1" />
                        <button className="text-yellow-400 hover:text-yellow-300 underline">
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 mb-4">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="flex items-center text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => goToPage(pageNumber)}
                  className={`w-8 h-8 flex items-center justify-center rounded ${
                    currentPage === pageNumber
                      ? "bg-[#FFCA20] text-black font-medium"
                      : "bg-gray-800 text-white hover:bg-gray-700"
                  }`}
                >
                  {pageNumber}
                </button>
              )
            )}
          </div>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="flex items-center text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ApplicationTable;
